import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { DatadogProvider } from '@datadog/openfeature-browser';
import { OpenFeature, OpenFeatureProvider } from '@openfeature/react-sdk';

import AppMessage from '@/components/app-message';
import { AppRoutes } from '@/components/app-routes';
import { Fallback } from '@/components/fallback';
import ShowCookieWarning from '@/ui/ShowCookieWarning';
import { DATADOG, SENTRY_STAGE } from '@/utils/constants';
import '@/index.css';

let localStorageAccessible = true;

try {
  localStorage.setItem('test-cookie', 'cookie');
  localStorage.removeItem('test-cookie');
} catch (e) {
  localStorageAccessible = false;
}

const container = document.getElementById('root')!;
const root = createRoot(container, {
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    // eslint-disable-next-line no-console
    console.warn('Uncaught error:', error, errorInfo.componentStack);
  }),
});
const provider = new DatadogProvider({
  applicationId: DATADOG.APPLICATION_ID,
  clientToken: DATADOG.CLIENT_TOKEN,
  env: SENTRY_STAGE,
  site: 'datadoghq.com',
});

OpenFeature.setProvider(provider);

function App() {
  return (
    <Suspense fallback={<Fallback />}>
      <OpenFeatureProvider suspendUntilReady>
        {localStorageAccessible ? <AppRoutes /> : <ShowCookieWarning />}
      </OpenFeatureProvider>
    </Suspense>
  );
}

root.render(
  <StrictMode>
    {import.meta.env.PROD ? (
      <Sentry.ErrorBoundary fallback={<AppMessage />}>
        <App />
      </Sentry.ErrorBoundary>
    ) : (
      <App />
    )}
  </StrictMode>,
);
